import { catchErrorHandler } from "@picovico/utils";
import type { WhatsAppOtpChannel } from "custom-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyGetMobileVerificationOtpQuery } from "store/services/user";
import {
  OTP_SMS_SENT,
  OtpSentInfo,
  SMS_FALLBACK_DELAY_MS,
  offersSmsFallback,
  otpThrottleMessage,
  readOtpSent,
} from "utils/whatsapp";
import { useAppToast } from "./useAppToast";

export interface MobileOtp {
  /**
   * Asks for a code (POST `/member/verify/otp/`): on the channel chosen
   * earlier ("Send by SMS instead"), else without a body so the server picks,
   * as before WhatsApp. Resolves with the answer, or null after telling the
   * customer why it failed.
   */
  send: (channel?: WhatsAppOtpChannel) => Promise<OtpSentInfo | null>;
  /** "Send by SMS instead": `{channel: "sms"}`, and every later code stays on SMS. */
  sendBySms: () => Promise<OtpSentInfo | null>;
  /** Back to the number step: the SMS offer and its timer stop. */
  reset: () => void;
  /** The last answer (its `channel` and fallback), null before the first and after `reset`. */
  sent: OtpSentInfo | null;
  /** Show "Send by SMS instead": `SMS_FALLBACK_DELAY_MS` after a WhatsApp code that has an SMS fallback. */
  smsOffered: boolean;
  isSending: boolean;
}

/**
 * The membership OTP request shared by the mobile verification modal and the
 * profile's "Mobile Verification" (claude/design/whatsapp-messaging/02 §10,
 * 04 §3, pvapp's verify screen). The screen follows the answer's `channel`,
 * not the `whatsapp.otp` flag; an older server (no `channel`) gets exactly
 * the old flow. Any 429 reads "Too many codes requested. Try again in N
 * seconds" from `retry_after` (the body's, or the `Retry-After` header).
 */
export function useMobileOtp(): MobileOtp {
  const [requestOtp, { isFetching }] = useLazyGetMobileVerificationOtpQuery();
  const { error: errorToast, success } = useAppToast();
  const [sent, setSent] = useState<OtpSentInfo | null>(null);
  const [smsOffered, setSmsOffered] = useState(false);
  // The channel the customer chose; undefined lets the server pick.
  const chosenChannel = useRef<WhatsAppOtpChannel | undefined>(undefined);

  // Every new answer (and `reset`) restarts the offer.
  useEffect(() => {
    setSmsOffered(false);
    if (!offersSmsFallback(sent)) return undefined;
    const timer = setTimeout(() => setSmsOffered(true), SMS_FALLBACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [sent]);

  const send = useCallback(
    async (channel?: WhatsAppOtpChannel): Promise<OtpSentInfo | null> => {
      const asked = channel ?? chosenChannel.current;
      try {
        const response = await requestOtp(
          asked ? { channel: asked } : undefined,
        ).unwrap();
        const info = readOtpSent(response);
        if (asked) chosenChannel.current = asked;
        setSent(info);
        return info;
      } catch (failure) {
        const throttled = otpThrottleMessage(failure);
        if (throttled) errorToast(throttled);
        else catchErrorHandler(failure as object, errorToast);
        return null;
      }
    },
    [requestOtp, errorToast],
  );

  const sendBySms = useCallback(async () => {
    const info = await send("sms");
    if (info) success(OTP_SMS_SENT);
    return info;
  }, [send, success]);

  const reset = useCallback(() => setSent(null), []);

  return { send, sendBySms, reset, sent, smsOffered, isSending: isFetching };
}
